import clsx from 'clsx';
import moment from 'moment';
import { IResumoBancario } from '../../interfaces/IModuloBancario';

export type EtapaId = 'preparar' | 'arquivos' | 'minutas' | 'fechar';
export type ConsultaId = 'indicadores' | 'trilha';
export type Situacao = 'concluida' | 'atencao' | 'andamento' | 'pendente';

export interface EtapaInfo {
    id: EtapaId;
    numero: number;
    titulo: string;
    descricao: string;
    situacao: Situacao;
    /** Curto, embaixo do título da etapa. */
    resumo: string;
    /** O que a pessoa deve fazer agora, nesta etapa. */
    agora: string;
}

const BASE: Pick<EtapaInfo, 'id' | 'numero' | 'titulo' | 'descricao'>[] = [
    { id: 'preparar', numero: 1, titulo: 'Preparar', descricao: 'Contas bancárias e Setup: quais contas entram e para qual conta contábil vai cada categoria recorrente do extrato.' },
    { id: 'arquivos', numero: 2, titulo: 'Arquivos', descricao: 'Extratos, obrigatórios, e documentos, opcionais. Nenhum documento gera minuta sozinho: ele só explica uma linha que já está no extrato.' },
    { id: 'minutas', numero: 3, titulo: 'Minutas', descricao: 'Para cada linha do extrato sem lançamento no razão, o módulo propõe a contabilização. Você resolve as pendências e aprova.' },
    { id: 'fechar', numero: 4, titulo: 'Fechar e exportar', descricao: 'Fecha a conta na competência e gera o TXT para o Domínio. O razão reimportado confirma que o lançamento entrou.' },
];

const plural = (n: number, um: string, varios: string) => `${n.toLocaleString('pt-BR')} ${n === 1 ? um : varios}`;

/** Só orienta: nenhuma etapa é bloqueada aqui, e o servidor continua aplicando os impedimentos. */
export function calcularEtapas(r: IResumoBancario | null, competencia: string): EtapaInfo[] {
    // Antes da resposta, dizer "Nenhuma conta" seria afirmar o que ainda não se sabe.
    if (!r) return BASE.map(e => ({ ...e, situacao: 'pendente', resumo: 'Carregando…', agora: 'Carregando a situação desta etapa.' }));

    const contas = r?.contasAtivas ?? 0;
    const aConfigurar = (r?.categoriasAConfigurar ?? 0) + (r?.contasForaDoPlano ?? 0);

    const preparar: Pick<EtapaInfo, 'situacao' | 'resumo' | 'agora'> = contas === 0
        ? { situacao: 'pendente', resumo: 'Nenhuma conta', agora: 'Cadastre as contas bancárias da empresa, ligando cada uma à conta contábil de Banco ou Caixa.' }
        : aConfigurar > 0
            ? { situacao: 'atencao', resumo: plural(aConfigurar, 'item a configurar', 'itens a configurar'), agora: (r?.contasForaDoPlano ?? 0) > 0 ? 'Há conta bancária com alerta: a conta contábil dela não existe no plano deste período. Corrija antes de seguir.' : 'Complete as categorias marcadas como incompletas, ou inative as que a empresa não usa.' }
            : { situacao: 'concluida', resumo: plural(contas, 'conta', 'contas'), agora: 'Tudo configurado. Siga para os arquivos.' };

    const aguardando = r?.extratosAguardando ?? 0;
    const retidos = r?.extratosRetidos ?? 0;
    const prontos = r?.extratosProntos ?? 0;
    const aceitos = r?.extratosAceitos ?? 0;
    const arquivos: Pick<EtapaInfo, 'situacao' | 'resumo' | 'agora'> = r?.extratosEmAndamento
        ? { situacao: 'andamento', resumo: 'Processando', agora: 'Os extratos estão sendo processados. A lista se atualiza sozinha.' }
        : retidos > 0
            ? { situacao: 'atencao', resumo: plural(retidos, 'retido', 'retidos'), agora: 'Extrato retido trava a conta: veja o motivo na lista e descarte o arquivo para enviar o correto.' }
            : aguardando > 0
                ? { situacao: 'atencao', resumo: plural(aguardando, 'aguardando você', 'aguardando você'), agora: 'Alguns extratos precisam da conta ou da confirmação do saldo. Use as ações na lista.' }
                : prontos > 0
                    ? { situacao: 'atencao', resumo: plural(prontos, 'pronto', 'prontos'), agora: 'Clique em Iniciar processamento para gravar os extratos prontos.' }
                    : aceitos > 0
                        ? { situacao: 'concluida', resumo: plural(aceitos, 'extrato aceito', 'extratos aceitos'), agora: 'Extratos aceitos. Envie documentos se quiser que eles expliquem mais linhas; eles são opcionais.' }
                        : { situacao: 'pendente', resumo: 'Nenhum extrato', agora: 'Envie o extrato de cada conta bancária, em OFX ou PDF. O PDF é lido por IA, com o custo informado antes.' };

    const pendentes = r?.minutasPendentes ?? 0;
    const geradas = r?.minutasGeradas ?? 0;
    const decididas = (r?.minutasAprovadas ?? 0) + (r?.minutasReprovadas ?? 0) + (r?.minutasExportadas ?? 0) + (r?.minutasContabilizadas ?? 0);
    const mes = competencia ? moment(competencia, 'YYYY-MM').format('MM/YYYY') : '';

    const minutas: Pick<EtapaInfo, 'situacao' | 'resumo' | 'agora'> = !competencia
        ? { situacao: 'pendente', resumo: 'Escolha o mês', agora: 'Escolha a competência para gerar e aprovar as minutas.' }
        : r?.geracaoEmAndamento
            ? { situacao: 'andamento', resumo: 'Gerando', agora: `As minutas de ${mes} estão sendo geradas. A lista se atualiza sozinha.` }
            : pendentes > 0
                ? { situacao: 'atencao', resumo: plural(pendentes, 'pendente', 'pendentes'), agora: 'Comece pelas pendentes: elas dizem o que falta para explicar a linha. Depois aprove as geradas.' }
                : geradas > 0
                    ? { situacao: 'atencao', resumo: plural(geradas, 'para aprovar', 'para aprovar'), agora: 'Confira as minutas geradas e aprove, uma a uma ou em lote.' }
                    : decididas > 0
                        ? { situacao: 'concluida', resumo: plural(decididas, 'decidida', 'decididas'), agora: 'Nenhuma minuta aguardando decisão. Siga para o fechamento.' }
                        : { situacao: 'pendente', resumo: 'Não geradas', agora: `Clique em Gerar minutas para propor os lançamentos de ${mes}.` };

    const aFechar = r?.contasAFechar ?? 0;
    const exportadas = r?.contasExportadas ?? 0;
    const fechar: Pick<EtapaInfo, 'situacao' | 'resumo' | 'agora'> = !competencia
        ? { situacao: 'pendente', resumo: 'Escolha o mês', agora: 'Escolha a competência para fechar as contas.' }
        : aFechar > 0 && exportadas >= aFechar
            ? { situacao: 'concluida', resumo: 'Exportado', agora: 'Importe o TXT no Domínio e reimporte o razão: as minutas viram Contabilizadas.' }
            : pendentes + geradas > 0
                ? { situacao: 'pendente', resumo: plural(pendentes + geradas, 'minuta antes', 'minutas antes'), agora: 'Ainda há minutas sem decisão. A conta só fecha sem pendentes e sem geradas.' }
                : aFechar > 0
                    ? { situacao: 'atencao', resumo: `${exportadas} de ${aFechar} exportadas`, agora: 'Feche cada conta bancária e exporte o TXT para o Domínio.' }
                    : { situacao: 'pendente', resumo: 'Sem extrato aceito', agora: 'A conta precisa de ao menos um extrato aceito na competência para fechar.' };

    return [
        { ...BASE[0], ...preparar },
        { ...BASE[1], ...arquivos },
        { ...BASE[2], ...minutas },
        { ...BASE[3], ...fechar },
    ];
}

const ICONE: Record<Situacao, string> = {
    concluida: 'fa-check',
    atencao: 'fa-exclamation',
    andamento: 'fa-sync-alt fa-spin',
    pendente: '',
};

const COR: Record<Situacao, string> = {
    concluida: 'bg-success text-white',
    atencao: 'bg-warning text-white',
    andamento: 'bg-primary text-white',
    pendente: 'bg-light text-gray-600',
};

const TEXTO: Record<Situacao, string> = {
    concluida: 'text-success',
    atencao: 'text-warning',
    andamento: 'text-primary',
    pendente: 'text-muted',
};

interface StepperProps {
    etapas: EtapaInfo[];
    ativa: EtapaId | ConsultaId;
    onEscolher: (id: EtapaId | ConsultaId) => void;
}

export function StepperModulo({ etapas, ativa, onEscolher }: StepperProps) {
    return (
        <div className='card card-flush mb-4'>
            <div className='card-body py-3 d-flex flex-wrap align-items-stretch gap-3'>
                <ol className='list-unstyled d-flex flex-wrap flex-grow-1 mb-0 gap-2' style={{ minWidth: 0 }}>
                    {etapas.map(e => {
                        const selecionada = ativa === e.id;
                        return (
                            <li key={e.id} className='d-flex' style={{ flex: '1 1 150px', minWidth: 0 }}>
                                <button type='button' aria-current={selecionada ? 'step' : undefined} onClick={() => onEscolher(e.id)}
                                    className={clsx('btn btn-flush d-flex align-items-center gap-3 w-100 rounded px-3 py-2 text-start', selecionada ? 'bg-light-primary' : 'bg-hover-light')}>
                                    <span className={clsx('d-flex align-items-center justify-content-center rounded-circle fw-bold flex-shrink-0', COR[e.situacao])} style={{ width: 32, height: 32 }}>
                                        {ICONE[e.situacao] ? <i className={clsx('fas fs-7 text-white', ICONE[e.situacao])}></i> : e.numero}
                                    </span>
                                    <span style={{ minWidth: 0 }}>
                                        <span className={clsx('d-block fw-bold fs-6', selecionada ? 'text-primary' : 'text-gray-800')}>{e.titulo}</span>
                                        <span className={clsx('d-block fs-8 text-truncate', TEXTO[e.situacao])}>{e.resumo}</span>
                                    </span>
                                </button>
                            </li>
                        );
                    })}
                </ol>
                <div className='d-flex align-items-center gap-2 border-start ps-3'>
                    <span className='text-muted fs-8 text-uppercase fw-semibold'>Consulta</span>
                    <button type='button' className={clsx('btn btn-sm', ativa === 'indicadores' ? 'btn-light-primary' : 'btn-light')} aria-current={ativa === 'indicadores' ? 'page' : undefined} onClick={() => onEscolher('indicadores')}>
                        <i className='fas fa-chart-bar me-1'></i>Indicadores
                    </button>
                    <button type='button' className={clsx('btn btn-sm', ativa === 'trilha' ? 'btn-light-primary' : 'btn-light')} aria-current={ativa === 'trilha' ? 'page' : undefined} onClick={() => onEscolher('trilha')}>
                        <i className='fas fa-history me-1'></i>Trilha
                    </button>
                </div>
            </div>
        </div>
    );
}

export function CabecalhoEtapa({ etapa }: { etapa: EtapaInfo }) {
    return (
        <div className='mb-4 px-1'>
            <h2 className='fs-3 fw-bold mb-1'>{etapa.numero}. {etapa.titulo}</h2>
            <div className='text-muted fs-6 mb-3'>{etapa.descricao}</div>
            <div className={clsx('d-flex align-items-start gap-2 rounded p-3 fs-7', etapa.situacao === 'atencao' ? 'bg-light-warning' : etapa.situacao === 'concluida' ? 'bg-light-success' : 'bg-light-primary')}>
                <i className={clsx('fas fa-arrow-right mt-1', TEXTO[etapa.situacao])}></i>
                <span><strong>O que fazer agora:</strong> {etapa.agora}</span>
            </div>
        </div>
    );
}

export function RodapeEtapa({ etapas, ativa, onEscolher }: { etapas: EtapaInfo[]; ativa: EtapaId; onEscolher: (id: EtapaId) => void }) {
    const i = etapas.findIndex(e => e.id === ativa);
    const anterior = etapas[i - 1];
    const proxima = etapas[i + 1];
    return (
        <div className='d-flex justify-content-between mt-2 mb-4'>
            {anterior
                ? <button className='btn btn-light' onClick={() => onEscolher(anterior.id)}><i className='fas fa-arrow-left me-2'></i>{anterior.titulo}</button>
                : <span></span>}
            {proxima && <button className='btn btn-primary' onClick={() => onEscolher(proxima.id)}>Próximo: {proxima.titulo}<i className='fas fa-arrow-right ms-2'></i></button>}
        </div>
    );
}

/** As etapas que dependem do mês pedem a competência aqui, em vez de mostrar uma lista vazia. */
export function EscolherCompetencia({ competencias, onEscolher, motivo }: { competencias: string[]; onEscolher: (c: string) => void; motivo: string }) {
    return (
        <div className='card card-flush mb-4'>
            <div className='card-body text-center py-10'>
                <i className='fas fa-calendar-day fs-1 text-primary mb-3'></i>
                <h3 className='fw-bold mb-2'>Escolha a competência</h3>
                <div className='text-muted mb-5'>{motivo}</div>
                <div className='d-flex flex-wrap justify-content-center gap-2'>
                    {competencias.map(c => (
                        <button key={c} className='btn btn-sm btn-light-primary' onClick={() => onEscolher(c)}>{moment(c, 'YYYY-MM').format('MM/YYYY')}</button>
                    ))}
                </div>
            </div>
        </div>
    );
}
